/**
 * UnstyledLink
 *
 * A lightweight wrapper around <Link> and <a>, allowing:
 * - Internal links using Next.js <Link>
 * - External links using <a target="_blank">
 * - Optional forced new-tab behavior
 * - Pass-through of all anchor attributes
 *
 * Fully compatible with:
 * - Next.js 13/14 (app or pages router)
 * - Node 20–24
 */

import clsx from "clsx";
import Link from "next/link";
import * as React from "react";

export type UnstyledLinkProps = {
  /** The URL to navigate to */
  href: string;

  /** The link contents */
  children: React.ReactNode;

  /** Force opening in a new tab */
  openNewTab?: boolean;

  /** Additional classes applied to the <a> or Link-rendered <a> */
  className?: string;
} & Omit<React.ComponentPropsWithoutRef<"a">, "href" | "children">;

export default function UnstyledLink({
  children,
  href,
  openNewTab,
  className,
  ...rest
}: UnstyledLinkProps) {
  /**
   * Determine if link should open in a new tab:
   * - User forced it via openNewTab
   * - OR URL is external (not relative "/" or "#")
   */
  const isExternal =
    typeof href === "string" &&
    !href.startsWith("/") &&
    !href.startsWith("#");

  const isNewTab = openNewTab ?? isExternal;

  /* -------------------------------------------------------------------------- */
  /*                           INTERNAL NEXT.JS LINK                             */
  /* -------------------------------------------------------------------------- */
  if (!isNewTab) {
    return (
      <Link href={href} className={className} {...rest}>
        {children}
      </Link>
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                               EXTERNAL LINK                                */
  /* -------------------------------------------------------------------------- */
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx(className, "cursor-newtab")}
      {...rest}
    >
      {children}
    </a>
  );
}
